import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Share2, Copy, Trophy, Crown, Flag, Target, Camera, Flame, Zap, TrendingUp, CircleDot, Frown, Medal } from "lucide-react";
import { toPng } from "html-to-image";
import { dayLeaderboard, championLeaderboard, playerDayTotals, stablefordPoints, initials, dayHandicap } from "@/lib/scoring";

/**
 * Shareable results sheet with paginated pages.
 * Modes:
 *  - "day": Day leaderboard + FH/GIR/Scores tables for that day only
 *  - "championship": Overall + FH/GIR/Scores round-by-round across all finalized days
 */

const BASE_PAGES = ["Leaderboard", "Fairways Hit", "Greens in Reg.", "Putting", "Scores by Round"];
const avgPutts = (putts, holes) => (holes ? (putts / holes).toFixed(1) : "—");

function pct(n, d) { return d ? Math.round((n / d) * 100) : null; }

// Longest consecutive holes of par-or-better (Stableford points >= 2) for a player on a day.
function longestStreakForPlayerDay(player, day, event) {
  const course = event.courses[day.courseKey];
  if (!course) return 0;
  const group = (day.groups || []).find((g) => (g.playerIds || []).includes(player.id));
  if (!group) return 0;
  const scores = (group.scores && group.scores[player.id]) || [];
  let best = 0, cur = 0;
  for (let i = 0; i < 18; i++) {
    const s = scores[i];
    if (s && s.strokes) {
      const pts = stablefordPoints(s.strokes, course.par[i], dayHandicap(day, player), course.si[i]);
      if (pts >= 2) { cur += 1; if (cur > best) best = cur; }
      else cur = 0;
    } else cur = 0;
  }
  return best;
}

// Best single-hole Stableford points player-day-hole.
function bestHoleForDay(day, event) {
  const course = event.courses[day.courseKey];
  if (!course) return null;
  let best = null;
  for (const g of day.groups || []) {
    for (const pid of g.playerIds || []) {
      const player = (event.players || []).find((p) => p.id === pid);
      if (!player) continue;
      const scores = g.scores?.[pid] || [];
      for (let i = 0; i < 18; i++) {
        const s = scores[i];
        if (!s || !s.strokes) continue;
        const pts = stablefordPoints(s.strokes, course.par[i], dayHandicap(day, player), course.si[i]);
        if (!best || pts > best.points) best = { player, points: pts, holeIdx: i };
      }
    }
  }
  return best;
}

function computeCallouts({ mode, day, event, overall, matrix, rounds }) {
  if (!overall || overall.length === 0) return null;
  const players = event.players || [];

  // Champion / winner of the sheet
  const winner = overall[0];
  const champion = mode === "day"
    ? { player: winner.player, sub: `${winner.points} pts · ${winner.strokes} str` }
    : { player: winner.player, sub: `${winner.championPoints.toFixed(1)} CP · ${winner.daysPlayed} rd${winner.daysPlayed === 1 ? "" : "s"}` };

  // Longest streak — check across all finalized days (or the single day)
  const daysForStreak = mode === "day" ? (day ? [day] : []) : (event.days || []).filter((d) => d.finalized);
  let streak = null;
  for (const p of players) {
    for (const d of daysForStreak) {
      const len = longestStreakForPlayerDay(p, d, event);
      if (len >= 2 && (!streak || len > streak.length)) streak = { player: p, length: len, roundLabel: d.label };
    }
  }

  // Biggest points jump / best hole
  let jump = null;
  if (mode === "day" && day) {
    const bh = bestHoleForDay(day, event);
    if (bh && bh.points > 0) jump = { player: bh.player, value: bh.points, label: `H${bh.holeIdx + 1} · Stableford` };
  } else {
    // biggest single-round points across all matrix rounds
    for (const row of matrix) {
      row.perRound.forEach((t, k) => {
        if (t && t.holesPlayed && (!jump || t.points > jump.value)) {
          jump = { player: row.player, value: t.points, label: rounds[k]?.label || "Round" };
        }
      });
    }
  }

  // Most GIRs
  let girTop = null;
  for (const row of matrix) {
    if (row.girHit > 0 && (!girTop || row.girHit > girTop.hit || (row.girHit === girTop.hit && row.girHoles < girTop.holes))) {
      girTop = { player: row.player, hit: row.girHit, holes: row.girHoles };
    }
  }

  // Best putter: lowest average over at least 9 recorded holes
  let putter = null;
  for (const row of matrix) {
    if (row.puttHoles >= 9) {
      const avg = row.putts / row.puttHoles;
      if (!putter || avg < putter.avg || (avg === putter.avg && row.onePutts > putter.onePutts)) {
        putter = { player: row.player, avg, onePutts: row.onePutts, holes: row.puttHoles };
      }
    }
  }

  // Worst greens: most three-putts (ties → higher average)
  let threePutter = null;
  for (const row of matrix) {
    if (row.threePutts > 0 && (!threePutter || row.threePutts > threePutter.count || (row.threePutts === threePutter.count && row.putts / row.puttHoles > threePutter.avg))) {
      threePutter = { player: row.player, count: row.threePutts, avg: row.putts / row.puttHoles };
    }
  }

  return { champion, streak, jump, gir: girTop, putter, threePutter };
}

// Build per-day per-player totals matrix for the given day list.
function buildRoundMatrix(event, days) {
  const players = event.players || [];
  const rounds = days.map((d) => ({ id: d.id, label: d.label, courseName: event.courses[d.courseKey]?.name || "" }));
  const byPid = {};
  for (const p of players) byPid[p.id] = { player: p, rounds: {} };
  for (const d of days) {
    const course = event.courses[d.courseKey];
    if (!course) continue;
    for (const g of d.groups || []) {
      for (const pid of g.playerIds || []) {
        const player = players.find((x) => x.id === pid);
        if (!player || !byPid[pid]) continue;
        byPid[pid].rounds[d.id] = playerDayTotals(player, g, course, d);
      }
    }
  }
  const rows = Object.values(byPid).map((r) => {
    const perRound = rounds.map((ri) => r.rounds[ri.id] || null);
    const fhHit = perRound.reduce((a, t) => a + (t?.fhHit || 0), 0);
    const fhAtt = perRound.reduce((a, t) => a + (t?.fhAttempts || 0), 0);
    const fhRecorded = perRound.reduce((a, t) => a + (t?.fhRecorded || 0), 0);
    const girHit = perRound.reduce((a, t) => a + (t?.girHit || 0), 0);
    const girHoles = perRound.reduce((a, t) => a + (t?.girHoles || 0), 0);
    const points = perRound.reduce((a, t) => a + (t?.points || 0), 0);
    const strokes = perRound.reduce((a, t) => a + (t?.strokes || 0), 0);
    const holesPlayed = perRound.reduce((a, t) => a + (t?.holesPlayed || 0), 0);
    const putts = perRound.reduce((a, t) => a + (t?.putts || 0), 0);
    const puttHoles = perRound.reduce((a, t) => a + (t?.puttHoles || 0), 0);
    const onePutts = perRound.reduce((a, t) => a + (t?.onePutts || 0), 0);
    const threePutts = perRound.reduce((a, t) => a + (t?.threePutts || 0), 0);
    return { ...r, perRound, fhHit, fhAtt: fhRecorded ? fhAtt : 0, girHit, girHoles, points, strokes, holesPlayed, putts, puttHoles, onePutts, threePutts };
  });
  return { rounds, rows };
}

export default function ResultsSheetModal({ open, onClose, event, mode = "day", day = null }) {
  const [page, setPage] = useState(0);
  const [capturing, setCapturing] = useState(false);
  const cardRef = useRef(null);
  const bodyRef = useRef(null);

  const dayList = useMemo(() => {
    if (mode === "day") return day ? [day] : [];
    return (event?.days || []).filter((d) => d.finalized);
  }, [mode, day, event]);

  const { rounds, rows: matrix } = useMemo(() => buildRoundMatrix(event || { players: [], courses: {} }, dayList), [event, dayList]);

  const overall = useMemo(() => {
    if (!event) return [];
    if (mode === "day" && day) return dayLeaderboard(day, event);
    if (mode === "championship") return championLeaderboard(event);
    return [];
  }, [mode, day, event]);

  const callouts = useMemo(
    () => (event ? computeCallouts({ mode, day, event, overall, matrix, rounds }) : null),
    [mode, day, event, overall, matrix, rounds]
  );

  if (!open) return null;

  const bestFh = [...matrix].sort((a, b) => {
    const ap = a.fhAtt ? a.fhHit / a.fhAtt : -1;
    const bp = b.fhAtt ? b.fhHit / b.fhAtt : -1;
    return bp - ap || b.fhHit - a.fhHit;
  });
  const bestGir = [...matrix].sort((a, b) => {
    const ap = a.girHoles ? a.girHit / a.girHoles : -1;
    const bp = b.girHoles ? b.girHit / b.girHoles : -1;
    return bp - ap || b.girHit - a.girHit;
  });
  const bestScore = [...matrix].sort((a, b) => b.points - a.points || a.strokes - b.strokes);
  const bestPutting = [...matrix].sort((a, b) => {
    const ap = a.puttHoles ? a.putts / a.puttHoles : Infinity;
    const bp = b.puttHoles ? b.putts / b.puttHoles : Infinity;
    return ap - bp || b.onePutts - a.onePutts;
  });

  const title = mode === "day" ? `${day?.label || "Day"} Results` : "Championship Results";

  // End-of-trip awards (championship mode only)
  const awards = mode === "championship" && callouts ? [
    { id: "champion", label: "Champion", icon: <Crown size={14} />, name: callouts.champion?.player.name, sub: callouts.champion?.sub },
    overall[1] ? { id: "runner-up", label: "Runner-up", icon: <Trophy size={14} />, name: overall[1].player.name, sub: `${overall[1].championPoints.toFixed(1)} CP` } : null,
    callouts.putter ? { id: "best-putter", label: "Best putter", icon: <CircleDot size={14} />, name: callouts.putter.player.name, sub: `${callouts.putter.avg.toFixed(1)} putts/hole · ${callouts.putter.onePutts} one-putts` } : null,
    callouts.gir ? { id: "most-girs", label: "Most GIRs", icon: <Target size={14} />, name: callouts.gir.player.name, sub: `${callouts.gir.hit}/${callouts.gir.holes} greens · ${pct(callouts.gir.hit, callouts.gir.holes)}%` } : null,
    bestFh[0]?.fhAtt ? { id: "fairway-finder", label: "Fairway finder", icon: <Flag size={14} />, name: bestFh[0].player.name, sub: `${bestFh[0].fhHit}/${bestFh[0].fhAtt} fairways · ${pct(bestFh[0].fhHit, bestFh[0].fhAtt)}%` } : null,
    callouts.threePutter ? { id: "worst-greens", label: "Worst greens", icon: <Frown size={14} />, name: callouts.threePutter.player.name, sub: `${callouts.threePutter.count} three-putts · ${callouts.threePutter.avg.toFixed(1)} putts/hole` } : null,
    callouts.streak ? { id: "longest-streak", label: "Longest streak", icon: <Flame size={14} />, name: callouts.streak.player.name, sub: `${callouts.streak.length} holes par-or-better · ${callouts.streak.roundLabel}` } : null,
    callouts.jump ? { id: "biggest-round", label: "Biggest round", icon: <TrendingUp size={14} />, name: callouts.jump.player.name, sub: `${callouts.jump.value} pts · ${callouts.jump.label}` } : null,
  ].filter(Boolean) : [];
  const pages = mode === "championship" ? ["Awards", ...BASE_PAGES] : BASE_PAGES;
  const pageKey = pages[Math.min(page, pages.length - 1)];
  const subtitle = mode === "day"
    ? (event.courses[day?.courseKey]?.name || "")
    : `${event.name} · ${rounds.length} round${rounds.length === 1 ? "" : "s"}`;

  const buildShareText = () => {
    const lines = [];
    lines.push(`🏆 ${event.name} — ${title}`);
    if (subtitle) lines.push(subtitle);
    lines.push("");
    lines.push("Leaderboard");
    overall.slice(0, 20).forEach((r, i) => {
      if (mode === "day") {
        lines.push(`${i + 1}. ${r.player.name} — ${r.points} pts (${r.strokes} str)`);
      } else {
        lines.push(`${i + 1}. ${r.player.name} — ${r.championPoints.toFixed(1)} CP`);
      }
    });
    if (awards.length > 0) {
      lines.push("");
      lines.push("🏅 Trip awards");
      awards.forEach((a) => lines.push(`${a.label}: ${a.name}${a.sub ? ` — ${a.sub}` : ""}`));
    }
    if (rounds.length > 0) {
      lines.push("");
      lines.push("Fairways hit");
      bestFh.forEach((r, i) => {
        if (!r.fhAtt) return;
        lines.push(`${i + 1}. ${r.player.name} — ${r.fhHit}/${r.fhAtt} (${pct(r.fhHit, r.fhAtt)}%)`);
      });
      lines.push("");
      lines.push("Greens in regulation");
      bestGir.forEach((r, i) => {
        if (!r.girHoles) return;
        lines.push(`${i + 1}. ${r.player.name} — ${r.girHit}/${r.girHoles} (${pct(r.girHit, r.girHoles)}%)`);
      });
      lines.push("");
      lines.push("Putting");
      bestPutting.forEach((r, i) => {
        if (!r.puttHoles) return;
        lines.push(`${i + 1}. ${r.player.name} — ${avgPutts(r.putts, r.puttHoles)} avg · ${r.onePutts} one-putt${r.onePutts === 1 ? "" : "s"} · ${r.threePutts} three-putt${r.threePutts === 1 ? "" : "s"}`);
      });
    }
    lines.push("");
    lines.push(`${window.location.origin}/trip/${event.id}`);
    return lines.join("\n");
  };

  const doShare = async () => {
    const text = buildShareText();
    try {
      if (navigator.share) {
        await navigator.share({ title, text });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Share not supported");
    }
  };

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText());
      toast.success("Copied to clipboard");
    } catch { toast.error("Clipboard blocked"); }
  };

  const doSaveImage = async () => {
    if (!cardRef.current) return;
    setCapturing(true);
    // Temporarily expand the scroll body so nothing gets clipped in the capture.
    const cardEl = cardRef.current;
    const bodyEl = bodyRef.current;
    const prevMax = cardEl.style.maxHeight;
    const prevBodyOverflow = bodyEl ? bodyEl.style.overflow : "";
    const prevBodyMax = bodyEl ? bodyEl.style.maxHeight : "";
    cardEl.style.maxHeight = "none";
    if (bodyEl) { bodyEl.style.overflow = "visible"; bodyEl.style.maxHeight = "none"; }
    try {
      const dataUrl = await toPng(cardEl, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#0f1e15",
      });
      const filename = `${event.name?.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "trip"}-${mode === "day" ? (day?.label || "day").toLowerCase().replace(/\s+/g, "-") : "championship"}-${pageKey.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], filename, { type: "image/png" });
      // Try Web Share API with file
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title, text: title });
          return;
        } catch (e) {
          if (e?.name !== "AbortError") { /* fall through to download */ }
          else return;
        }
      }
      // Fallback: download
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Image saved");
    } catch (e) {
      toast.error("Could not save image");
    } finally {
      cardEl.style.maxHeight = prevMax;
      if (bodyEl) { bodyEl.style.overflow = prevBodyOverflow; bodyEl.style.maxHeight = prevBodyMax; }
      setCapturing(false);
    }
  };

  const goPrev = () => setPage((p) => Math.max(0, p - 1));
  const goNext = () => setPage((p) => Math.min(pages.length - 1, p + 1));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="results-sheet-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div ref={cardRef} className="relative w-full max-w-lg bg-[color:var(--bg-dark-green)] border border-[color:var(--border-brass)] rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col tga-fade">
        <div className="px-4 pt-3 pb-2 border-b border-[color:var(--border-brass-light)]">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              {mode === "day" ? <Trophy size={18} className="text-[color:var(--brass-bright)] shrink-0" /> : <Crown size={18} className="text-[color:var(--brass-bright)] shrink-0" />}
              <div className="min-w-0">
                <div className="font-display text-xl text-[color:var(--brass-bright)] truncate">{title}</div>
                {subtitle && <div className="text-[11px] text-[color:var(--muted-green)] truncate">{subtitle}</div>}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button data-testid="results-save-img-btn" onClick={doSaveImage} disabled={capturing} className="ghost-btn rounded-lg p-2 disabled:opacity-50" title="Save as image"><Camera size={16} /></button>
              <button data-testid="results-share-btn" onClick={doShare} className="ghost-btn rounded-lg p-2" title="Share"><Share2 size={16} /></button>
              <button data-testid="results-copy-btn" onClick={doCopy} className="ghost-btn rounded-lg p-2" title="Copy"><Copy size={16} /></button>
              <button data-testid="results-close-btn" onClick={onClose} className="ghost-btn rounded-lg p-2" title="Close"><X size={16} /></button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--brass-dark)]">Page {page + 1} of {pages.length}</div>
            <div className="text-xs text-[color:var(--brass-bright)]">· {pages[page]}</div>
          </div>
          <div className="mt-2 flex gap-1.5">
            {pages.map((_, i) => (
              <div key={i} className="flex-1 h-1 rounded-full" style={{ background: i <= page ? "var(--brass)" : "rgba(197,160,89,0.2)" }} />
            ))}
          </div>
        </div>

        <div ref={bodyRef} className="p-3 overflow-y-auto flex-1">
          {pageKey === "Awards" && (
            <AwardsBoard event={event} awards={awards} rounds={rounds} />
          )}
          {pageKey === "Leaderboard" && (
            <div className="space-y-3" data-testid="results-page-leaderboard">
              {callouts && mode === "day" && (
                <div className="grid grid-cols-2 gap-2" data-testid="results-callouts">
                  <CalloutCard
                    testid="callout-champion"
                    icon={<Crown size={14} />}
                    label={mode === "day" ? "Player of the round" : "Champion"}
                    name={callouts.champion.player.name}
                    sub={callouts.champion.sub}
                    accent
                  />
                  <CalloutCard
                    testid="callout-streak"
                    icon={<Flame size={14} />}
                    label="Longest streak"
                    name={callouts.streak?.player.name || "—"}
                    sub={callouts.streak ? `${callouts.streak.length} holes par-or-better${mode === "championship" ? ` · ${callouts.streak.roundLabel}` : ""}` : "No par-run yet"}
                  />
                  <CalloutCard
                    testid="callout-jump"
                    icon={mode === "day" ? <Zap size={14} /> : <TrendingUp size={14} />}
                    label={mode === "day" ? "Best hole" : "Biggest round"}
                    name={callouts.jump?.player.name || "—"}
                    sub={callouts.jump ? `${callouts.jump.value} pts · ${callouts.jump.label}` : "—"}
                  />
                  <CalloutCard
                    testid="callout-gir"
                    icon={<Target size={14} />}
                    label="Most GIRs"
                    name={callouts.gir?.player.name || "—"}
                    sub={callouts.gir ? `${callouts.gir.hit}/${callouts.gir.holes} greens${callouts.gir.holes ? ` · ${pct(callouts.gir.hit, callouts.gir.holes)}%` : ""}` : "—"}
                  />
                  {callouts.putter && (
                    <CalloutCard
                      testid="callout-putter"
                      icon={<CircleDot size={14} />}
                      label="Best putter"
                      name={callouts.putter.player.name}
                      sub={`${callouts.putter.avg.toFixed(1)} putts/hole · ${callouts.putter.onePutts} one-putt${callouts.putter.onePutts === 1 ? "" : "s"}`}
                    />
                  )}
                  {callouts.threePutter && (
                    <CalloutCard
                      testid="callout-three-putts"
                      icon={<Frown size={14} />}
                      label="Worst greens"
                      name={callouts.threePutter.player.name}
                      sub={`${callouts.threePutter.count} three-putt${callouts.threePutter.count === 1 ? "" : "s"} · ${callouts.threePutter.avg.toFixed(1)} putts/hole`}
                    />
                  )}
                </div>
              )}

              <div className="overflow-x-auto -mx-1">
              {overall.length === 0 ? (
                <p className="helptext text-[color:var(--muted-green)]">No results yet.</p>
              ) : (
                <table className="w-full text-sm font-mono-metrics">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[color:var(--border-brass-light)]">
                      <th className="py-1 px-2">#</th>
                      <th className="py-1 px-2">Player</th>
                      {mode === "day" ? (<>
                        <th className="py-1 px-2 text-right">Str</th>
                        <th className="py-1 px-2 text-right">FH</th>
                        <th className="py-1 px-2 text-right">GIR</th>
                        <th className="py-1 px-2 text-right" title="Average putts · one-putts">Putts</th>
                        <th className="py-1 px-2 text-right">Pts</th>
                      </>) : (<>
                        <th className="py-1 px-2 text-right">Days</th>
                        <th className="py-1 px-2 text-right">Pts</th>
                        <th className="py-1 px-2 text-right">Wins</th>
                        <th className="py-1 px-2 text-right">CP</th>
                      </>)}
                    </tr>
                  </thead>
                  <tbody>
                    {overall.map((r, i) => (
                      <tr key={r.player.id} className="border-b border-[color:var(--border-brass-light)]/40" data-testid={`results-lb-row-${r.player.id}`}>
                        <td className="py-2 px-2 text-[color:var(--brass-bright)]">{i + 1}</td>
                        <td className="py-2 px-2 flex items-center gap-2 text-[color:var(--text-cream)]">
                          <div className="avatar avatar-dark" style={{ width: 24, height: 24, fontSize: 11 }}>{initials(r.player.name)}</div>
                          <span className="font-sans">{r.player.name}</span>
                          {i === 0 && <Trophy size={12} className="text-[color:var(--brass)]" />}
                        </td>
                        {mode === "day" ? (<>
                          <td className="py-2 px-2 text-right">{r.strokes || "—"}</td>
                          <td className="py-2 px-2 text-right">{r.fhAttempts ? `${r.fhHit}/${r.fhAttempts}` : "—"}</td>
                          <td className="py-2 px-2 text-right">{r.holesPlayed ? `${r.girHit}/${r.holesPlayed}` : "—"}</td>
                          <td className="py-2 px-2 text-right whitespace-nowrap" data-testid={`results-lb-putts-${r.player.id}`}>
                            {r.puttHoles ? <>{avgPutts(r.putts, r.puttHoles)}<span className="text-[10px] opacity-70 ml-1">{r.onePutts}×1</span></> : "—"}
                          </td>
                          <td className="py-2 px-2 text-right font-bold text-[color:var(--brass-bright)]">{r.points}</td>
                        </>) : (<>
                          <td className="py-2 px-2 text-right">{r.daysPlayed}</td>
                          <td className="py-2 px-2 text-right">{r.points}</td>
                          <td className="py-2 px-2 text-right">{r.dayWins || 0}</td>
                          <td className="py-2 px-2 text-right font-bold text-[color:var(--brass-bright)]">{r.championPoints.toFixed(1)}</td>
                        </>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              </div>
            </div>
          )}

          {pageKey === "Fairways Hit" && (
            <RoundMatrixTable
              testid="results-page-fh"
              rounds={rounds}
              rows={bestFh}
              valueFn={(t) => t?.fhAttempts ? `${t.fhHit}/${t.fhAttempts}` : "—"}
              totalFn={(r) => r.fhAtt ? `${r.fhHit}/${r.fhAtt}` : "—"}
              totalSubFn={(r) => r.fhAtt ? `${pct(r.fhHit, r.fhAtt)}%` : ""}
              icon={<Flag size={14} />}
              label="Fairways hit"
            />
          )}
          {pageKey === "Greens in Reg." && (
            <RoundMatrixTable
              testid="results-page-gir"
              rounds={rounds}
              rows={bestGir}
              valueFn={(t) => t?.girHoles ? `${t.girHit}/${t.girHoles}` : "—"}
              totalFn={(r) => r.girHoles ? `${r.girHit}/${r.girHoles}` : "—"}
              totalSubFn={(r) => r.girHoles ? `${pct(r.girHit, r.girHoles)}%` : ""}
              icon={<Target size={14} />}
              label="Greens in regulation"
            />
          )}
          {pageKey === "Putting" && (
            <RoundMatrixTable
              testid="results-page-putting"
              rounds={rounds}
              rows={bestPutting}
              valueFn={(t) => t?.puttHoles ? avgPutts(t.putts, t.puttHoles) : "—"}
              subFn={(t) => t?.puttHoles ? `${t.onePutts}× one · ${t.threePutts}× three` : ""}
              totalFn={(r) => r.puttHoles ? avgPutts(r.putts, r.puttHoles) : "—"}
              totalSubFn={(r) => r.puttHoles ? `${r.onePutts}×1 · ${r.threePutts}×3 · ${r.putts}p` : ""}
              icon={<CircleDot size={14} />}
              label="Putting · average per hole"
            />
          )}
          {pageKey === "Scores by Round" && (
            <RoundMatrixTable
              testid="results-page-scores"
              rounds={rounds}
              rows={bestScore}
              valueFn={(t) => t?.holesPlayed ? `${t.points}` : "—"}
              subFn={(t) => t?.holesPlayed ? `${t.strokes} str` : ""}
              totalFn={(r) => `${r.points}`}
              totalSubFn={(r) => r.holesPlayed ? `${r.strokes} str` : ""}
              icon={<Trophy size={14} />}
              label="Scores by round · Stableford points"
            />
          )}
        </div>

        <div className="px-4 py-3 border-t border-[color:var(--border-brass-light)] flex items-center gap-2">
          <button data-testid="results-prev-btn" onClick={goPrev} disabled={page === 0}
            className="ghost-btn rounded-lg px-3 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-40">
            <ChevronLeft size={14} /> Prev
          </button>
          <div className="flex-1 text-center text-[11px] text-[color:var(--muted-green)]">{pages[page]}</div>
          <button data-testid="results-next-btn" onClick={goNext} disabled={page === pages.length - 1}
            className="ghost-btn rounded-lg px-3 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-40">
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function RoundMatrixTable({ testid, rounds, rows, valueFn, subFn, totalFn, totalSubFn, icon, label }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1 text-[color:var(--brass-bright)] mb-2 font-display text-lg">
        {icon} {label}
      </div>
      {rounds.length === 0 ? (
        <p className="helptext text-[color:var(--muted-green)]">No finalized rounds yet.</p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-xs font-mono-metrics">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[color:var(--border-brass-light)]">
                <th className="py-1 px-2">#</th>
                <th className="py-1 px-2">Player</th>
                {rounds.map((ri) => <th key={ri.id} className="py-1 px-2 text-right whitespace-nowrap">{ri.label}</th>)}
                <th className="py-1 px-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.player.id} className="border-b border-[color:var(--border-brass-light)]/40" data-testid={`${testid}-row-${r.player.id}`}>
                  <td className="py-2 px-2 text-[color:var(--brass-bright)]">{i + 1}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2 text-[color:var(--text-cream)]">
                      <div className="avatar avatar-dark" style={{ width: 22, height: 22, fontSize: 10 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                    </div>
                  </td>
                  {r.perRound.map((t, k) => (
                    <td key={k} className="py-2 px-2 text-right whitespace-nowrap">
                      <div>{valueFn(t)}</div>
                      {subFn && <div className="text-[10px] opacity-60">{subFn(t)}</div>}
                    </td>
                  ))}
                  <td className="py-2 px-2 text-right font-bold text-[color:var(--brass-bright)] whitespace-nowrap">
                    <div>{totalFn(r)}</div>
                    {totalSubFn && <div className="text-[10px] opacity-70">{totalSubFn(r)}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Poster-style awards board designed to be exported as a single image.
function AwardsBoard({ event, awards, rounds }) {
  const [headline, ...rest] = awards;
  return (
    <div
      data-testid="results-awards"
      className="relative overflow-hidden rounded-2xl border-2 border-[color:var(--brass)] p-4 pt-5"
      style={{ background: "radial-gradient(120% 90% at 50% 0%, #1b4a35 0%, #0f1e15 70%)" }}
    >
      <div className="absolute inset-[6px] rounded-xl border border-[rgba(197,160,89,0.35)] pointer-events-none" />
      <div className="text-center mb-4">
        <img src="/logo-rounded.png" alt="" className="w-12 h-12 rounded-xl mx-auto mb-2 border border-[rgba(197,160,89,0.5)]" crossOrigin="anonymous" />
        <div className="text-[10px] uppercase tracking-[0.35em] text-[color:var(--brass-dark)]">Trip awards</div>
        <div className="font-display text-2xl text-[color:var(--brass-bright)] leading-tight">{event.name}</div>
        <div className="text-[11px] text-[color:var(--muted-green)]">{rounds.length} round{rounds.length === 1 ? "" : "s"} · {rounds.map((r) => r.label).join(" · ")}</div>
      </div>

      {awards.length === 0 && <div className="text-center text-sm text-[color:var(--muted-green)] py-6">Finalise a day to unlock the awards.</div>}

      {headline && (
        <div className="text-center rounded-xl border border-[color:var(--brass)] bg-[rgba(197,160,89,0.12)] px-3 py-4 mb-3" data-testid={`award-${headline.id}`}>
          <Crown size={22} className="mx-auto text-[color:var(--brass-bright)] mb-1" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--brass-dark)]">{headline.label}</div>
          <div className="font-display text-4xl text-[color:var(--text-cream)] leading-tight">{headline.name}</div>
          <div className="text-xs font-mono-metrics text-[color:var(--brass)] mt-1">{headline.sub}</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        {rest.map((a) => (
          <div key={a.id} className="rounded-lg border border-[rgba(197,160,89,0.35)] bg-[rgba(255,255,255,0.03)] px-3 py-2.5" data-testid={`award-${a.id}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[color:var(--brass-dark)]">
              <span className="text-[color:var(--brass)]">{a.icon}</span>{a.label}
            </div>
            <div className="font-display text-xl text-[color:var(--text-cream)] leading-tight truncate">{a.name}</div>
            <div className="text-[10px] font-mono-metrics text-[color:var(--muted-green)] leading-snug">{a.sub}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-center text-[10px] uppercase tracking-[0.3em] text-[color:var(--brass-dark)]">TGA Golf · trip golf, tracked properly</div>
    </div>
  );
}

function CalloutCard({ testid, icon, label, name, sub, accent }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl p-2.5 border"
      style={{
        borderColor: accent ? "var(--brass)" : "var(--border-brass-light)",
        background: accent ? "rgba(197,160,89,0.10)" : "rgba(0,0,0,0.15)",
      }}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-[color:var(--brass-dark)]">
        <span className="text-[color:var(--brass-bright)]">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="mt-1 font-display text-base leading-tight text-[color:var(--text-cream)] truncate">{name}</div>
      <div className="text-[11px] text-[color:var(--muted-green)] truncate">{sub}</div>
    </div>
  );
}

